import React, { useRef, useEffect } from "react";

// Lightweight canvas-based connected-particles network.
// Tuned for performance — caps DPR and particle count.
export default function Particles({
    color = "#8B5CF6",
    linkColor = "#6C47FF",
    density = 0.00012,
    maxParticles = 70,
    speed = 0.25,
    className = "",
}) {
    const ref = useRef(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        let dpr = Math.min(window.devicePixelRatio || 1, 1.6);
        let w = 0, h = 0;
        let particles = [];
        let raf = 0;

        const resize = () => {
            const r = canvas.getBoundingClientRect();
            w = r.width;
            h = r.height;
            canvas.width = Math.floor(w * dpr);
            canvas.height = Math.floor(h * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            const target = Math.min(
                maxParticles,
                Math.floor(w * h * density),
            );
            particles = new Array(target).fill(0).map(() => ({
                x: Math.random() * w,
                y: Math.random() * h,
                vx: (Math.random() - 0.5) * speed,
                vy: (Math.random() - 0.5) * speed,
                r: Math.random() * 1.6 + 0.6,
            }));
        };
        resize();

        const onResize = () => {
            cancelAnimationFrame(raf);
            resize();
            raf = requestAnimationFrame(loop);
        };
        window.addEventListener("resize", onResize);

        const loop = () => {
            ctx.clearRect(0, 0, w, h);
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > w) p.vx *= -1;
                if (p.y < 0 || p.y > h) p.vy *= -1;
                ctx.beginPath();
                ctx.fillStyle = color;
                ctx.globalAlpha = 0.55;
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.globalAlpha = 1;
            const maxDist = 130;
            for (let i = 0; i < particles.length; i++) {
                for (let j = i + 1; j < particles.length; j++) {
                    const a = particles[i];
                    const b = particles[j];
                    const dx = a.x - b.x;
                    const dy = a.y - b.y;
                    const dist = Math.hypot(dx, dy);
                    if (dist < maxDist) {
                        const alpha = (1 - dist / maxDist) * 0.5;
                        ctx.strokeStyle = linkColor;
                        ctx.globalAlpha = alpha;
                        ctx.lineWidth = 0.6;
                        ctx.beginPath();
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                        ctx.stroke();
                    }
                }
            }
            ctx.globalAlpha = 1;
            raf = requestAnimationFrame(loop);
        };
        loop();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", onResize);
        };
    }, [color, linkColor, density, maxParticles, speed]);

    return (
        <canvas
            ref={ref}
            className={`absolute inset-0 w-full h-full ${className}`}
            aria-hidden
        />
    );
}
